import { Box, Typography, Alert, Snackbar, Badge } from "@mui/material";
import React, { useState } from "react";
import {
  useGetPendingAbsenceRequestsQuery,
  useApproveAbsenceRequestMutation,
  useDenyAbsenceRequestMutation,
} from "../../api/absenceRequestsApi";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import { ConfirmationDialog } from "../../components/common/ConfirmationDialog/ConfirmationDialog";
import { FormDialog } from "../../components/common/FormDialog/FormDialog";
import PageTitle from "../../components/common/PageTitle";
import { AbsenceIcon } from "../../components/common/pageIcons";
import { TextField } from "@mui/material";
import PendingRequestsList from "./components/PendingRequestsList";
import { ViewAbsenceRequestDialog } from "./components/ViewAbsenceRequestDialog";
import { AbsenceRequestResponseDto } from "../../types/absence";
import { extractAllErrors } from "../../utils/errorUtils";
import { useInfiniteDataTableItems } from "../../hooks/useInfiniteDataTableItems";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../constants/dataTable";

const AdminPendingRequestsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [requestToApprove, setRequestToApprove] = useState<AbsenceRequestResponseDto | null>(null);
  const [requestToDeny, setRequestToDeny] = useState<AbsenceRequestResponseDto | null>(null);
  const [requestToView, setRequestToView] = useState<AbsenceRequestResponseDto | null>(null);
  const [denialReason, setDenialReason] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: requestsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetPendingAbsenceRequestsQuery({
    pageNumber: page + 1,
    pageSize: DEFAULT_DATA_TABLE_PAGE_SIZE,
  });
  const { accumulatedItems: requests, resetAccumulatedItems } =
    useInfiniteDataTableItems<AbsenceRequestResponseDto>(requestsData?.items, page);

  const [approveRequest, { isLoading: isApproving, error: approveError }] = useApproveAbsenceRequestMutation();
  const [denyRequest, { isLoading: isDenying, error: denyError }] = useDenyAbsenceRequestMutation();

  const resetRequestsTable = () => {
    resetAccumulatedItems();
    setPage(0);
  };

  const handleViewDetails = (request: AbsenceRequestResponseDto) => {
    setRequestToView(request);
  };

  const handleApproveClick = (request: AbsenceRequestResponseDto) => {
    setRequestToApprove(request);
  };

  const handleDenyClick = (request: AbsenceRequestResponseDto) => {
    setRequestToDeny(request);
    setDenialReason("");
  };

  const handleConfirmApprove = async () => {
    if (!requestToApprove) return;

    try {
      await approveRequest(requestToApprove.id).unwrap();
      setSuccessMessage(`Approved ${requestToApprove.employeeName}'s ${requestToApprove.absenceCategoryName} request`);
      setRequestToApprove(null);
      resetRequestsTable();
      refetch();
    } catch (error) {
      console.error("Failed to approve request:", error);
      setRequestToApprove(null);
    }
  };

  const handleConfirmDeny = async () => {
    if (!requestToDeny || !denialReason.trim()) return;

    try {
      await denyRequest({
        id: requestToDeny.id,
        dto: { denialReason: denialReason.trim() },
      }).unwrap();
      setSuccessMessage(`Denied ${requestToDeny.employeeName}'s ${requestToDeny.absenceCategoryName} request`);
      setRequestToDeny(null);
      setDenialReason("");
      resetRequestsTable();
      refetch();
    } catch (error) {
      console.error("Failed to deny request:", error);
      setRequestToDeny(null);
      setDenialReason("");
    }
  };

  const handleCloseApproveDialog = () => {
    setRequestToApprove(null);
  };

  const handleCloseDenyDialog = () => {
    setRequestToDeny(null);
    setDenialReason("");
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const handleLoadMore = () => {
    if (requestsData?.hasNextPage && !isFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load pending absence requests. Please try again later.
        </Alert>
      </Box>
    );
  }

  const pendingCount = requestsData?.totalCount || 0;

  return (
    <Box sx={{ p: 2 }}>
        <PageTitle
          title="Pending Absence Requests"
          icon={<AbsenceIcon />}
          endAdornment={
            pendingCount > 0 ? (
              <Badge badgeContent={pendingCount} color="warning" />
            ) : undefined
          }
        />

        {pendingCount === 0 ? (
          <Alert severity="info">
            No pending absence requests at the moment.
          </Alert>
        ) : (
          <PendingRequestsList
            requests={requests}
            isLoading={isLoading && requests.length === 0}
            onApproveClick={handleApproveClick}
            onDenyClick={handleDenyClick}
            onViewDetailsClick={handleViewDetails}
            totalCount={requestsData?.totalCount}
            hasNextPage={requestsData?.hasNextPage}
            isLoadingMore={isFetching && requests.length > 0}
            onLoadMore={handleLoadMore}
          />
        )}

      {/* View Request Details Dialog */}
      <ViewAbsenceRequestDialog
        open={!!requestToView}
        request={requestToView}
        onClose={() => setRequestToView(null)}
      />

      {/* Approve Confirmation Dialog */}
      <ConfirmationDialog
        open={!!requestToApprove}
        title="Approve Absence Request"
        message={`Are you sure you want to approve ${
          requestToApprove?.employeeName || "this employee"
        }'s ${
          requestToApprove?.absenceCategoryName || "absence"
        } request from ${
          requestToApprove?.startDate
            ? new Date(requestToApprove.startDate).toLocaleDateString()
            : ""
        } to ${
          requestToApprove?.endDate
            ? new Date(requestToApprove.endDate).toLocaleDateString()
            : ""
        }${
          requestToApprove?.daysDeducted
            ? ` (${requestToApprove.daysDeducted} day${requestToApprove.daysDeducted === 1 ? "" : "s"})`
            : ""
        }?`}
        confirmLabel="Approve"
        cancelLabel="Cancel"
        confirmColor="success"
        onConfirm={handleConfirmApprove}
        onClose={handleCloseApproveDialog}
        isLoading={isApproving}
      />

      {/* Deny Dialog with Reason */}
      <FormDialog
        open={!!requestToDeny}
        title="Deny Absence Request"
        onClose={handleCloseDenyDialog}
        onSubmit={handleConfirmDeny}
        isLoading={isDenying}
        submitLabel="Deny Request"
        cancelLabel="Cancel"
        maxWidth="sm"
      >
        <Box>
          {denyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Box>
                {extractAllErrors(denyError).map((err, index) => (
                  <Box key={index}>• {err}</Box>
                ))}
              </Box>
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are denying {requestToDeny?.employeeName}'s{" "}
            {requestToDeny?.absenceCategoryName} request from{" "}
            {requestToDeny?.startDate
              ? new Date(requestToDeny.startDate).toLocaleDateString()
              : ""}{" "}
            to{" "}
            {requestToDeny?.endDate
              ? new Date(requestToDeny.endDate).toLocaleDateString()
              : ""}
            .
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Denial Reason"
            value={denialReason}
            onChange={(e) => setDenialReason(e.target.value)}
            required
            placeholder="Please provide a reason for denying this request..."
            size="small"
            error={!denialReason.trim() && isDenying}
            helperText={
              !denialReason.trim() && isDenying
                ? "Denial reason is required"
                : ""
            }
          />
        </Box>
      </FormDialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={successMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Approve Error Alert */}
      {approveError && (
        <Snackbar
          open={!!approveError}
          autoHideDuration={6000}
          onClose={() => {}}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => {}}>
            <Box>
              {extractAllErrors(approveError).map((err, index) => (
                <Box key={index}>• {err}</Box>
              ))}
            </Box>
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default AdminPendingRequestsPage;

